import {ArtfactKind, DbEntity, FieldEntity, FieldModelXrefEntity, FieldTypeKind, Metadata, ModelDbXrefEntity} from '@mrp-brasil/nick-data-commons';
import {BaseHook, Hook, HookClass, NickProcessArtfact} from '@mrp-brasil/nick-process-commons';
import {from, Observable, Observer, of} from 'rxjs';
import {Client, types} from 'cassandra-driver';
import {concatMap, filter, map, toArray} from 'rxjs/operators';
import {MongoClient, Db, Collection, CreateCollectionOptions} from 'mongodb';

@HookClass('database.mongo', '684176ab-baab-4ab1-bf18-060ae55c1623')
export class DatabaseMongoHook extends BaseHook {

    private static connections = new Map<string, Client>();
    private static connectionByModel = new Map<string, string>();

    @Hook('bb2d56bd-ce0a-42bc-8966-8dfd40971d12')
    public authenticate(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.dbId)
                    throw new Error('Invalid Database ID.');

                if (DatabaseMongoHook.connections.has(inputArtifact.object.dbId))
                    return of(DatabaseMongoHook.connections.get(inputArtifact.object.dbId));

                return this.service('DbService')
                    .pipe(concatMap((service: any) => {
                        return service.findOne(inputArtifact.object.dbId)
                            .pipe(map((dbEntity: DbEntity) => {
                                dbEntity.accessKey$ = service.getAccessKey(dbEntity);
                                return dbEntity;
                            }));
                    }))
                    .pipe(concatMap((dbEntity: DbEntity) => {
                        return dbEntity.accessKey$
                            .pipe(concatMap((access: any) => {


                                const _client = new MongoClient(access.url, {monitorCommands: false});
                                return from(_client.connect())
                                    .pipe(concatMap((_client: any) => {
                                        const client: any = _client.db(dbEntity.dbName);
                                        return this.service('ModelDbXrefService')
                                            .pipe(concatMap((service: any) => {
                                                return service.findModelsByDatabaseId(dbEntity.id);
                                            }))
                                            .pipe(map((models: any[]) => {
                                                client._models = models;
                                                client._modelsMap = new Map<string, any>();
                                                client._dbEntity = dbEntity;

                                                models.forEach((model: any) => {
                                                    DatabaseMongoHook.connectionByModel.set(model.modelId, dbEntity.id);
                                                    client._modelsMap.set(model.modelId, model.model);
                                                });

                                                // todo: store connection with keyspace in hash key name
                                                DatabaseMongoHook.connections.set(dbEntity.id, client);
                                                return client;
                                            }));
                                    }));
                            }));
                    }));
            }))
            .pipe(map(() => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = {success: true};
                return outputArtifact;
            }));
    }

    @Hook('e9405ee2-e83b-4ad4-83a9-37052b1517bb')
    public query(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.dbId)
                    throw new Error('Invalid modelId');

                if (DatabaseMongoHook.connections.has(inputArtifact.object.dbId))
                    return of(DatabaseMongoHook.connections.get(inputArtifact.object.dbId));

                const connectArtifact = new NickProcessArtfact();
                connectArtifact.kind = ArtfactKind.ANY;
                connectArtifact.object = {dbId: inputArtifact.object.dbId};

                return this.authenticate(connectArtifact)
                    .pipe(map(() => DatabaseMongoHook.connections.get(inputArtifact.object.dbId)));
            }))
            .pipe(concatMap((client: any) => {
                return from(client.execute(inputArtifact.object.query, inputArtifact.object.params));
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result;
                return outputArtifact;
            }));
    }

    @Hook('09d4fef2-f20d-453e-ad19-b4da33b1de68')
    public sync(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.dbId)
                    throw new Error('Invalid dbId');

                if (DatabaseMongoHook.connections.has(inputArtifact.object.dbId))
                    return of(DatabaseMongoHook.connections.get(inputArtifact.object.dbId));

                const connectArtifact = new NickProcessArtfact();
                connectArtifact.kind = ArtfactKind.ANY;
                connectArtifact.object = {dbId: inputArtifact.object.dbId};

                return this.authenticate(connectArtifact)
                    .pipe(map(() => DatabaseMongoHook.connections.get(inputArtifact.object.dbId)));
            }))
            .pipe(concatMap((client: any) => {
                return from(Array.from(client._modelsMap.values()))
                    .pipe(concatMap((model: any) => {
                        const createArtifact = new NickProcessArtfact();
                        createArtifact.kind = ArtfactKind.ANY;
                        createArtifact.object = {modelId: model.id};

                        return this.createTable(createArtifact);
                    }), toArray());
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result;
                return outputArtifact;
            }));
    }


    @Hook('95cdcc6b-20d0-4ac1-a965-6c93ae5a5ed1')
    public createTable(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.modelId)
                    throw new Error('Invalid modelId');

                const connect$ = (dbId: string) => of(inputArtifact)
                    .pipe(concatMap(() => {
                        if (DatabaseMongoHook.connectionByModel.has(inputArtifact.object.modelId))
                            return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));

                        const connectArtifact = new NickProcessArtfact();
                        connectArtifact.kind = ArtfactKind.ANY;
                        connectArtifact.object = {dbId};

                        return this.authenticate(connectArtifact);
                    }));


                return this.service('ModelDbXrefService')
                    .pipe(concatMap((service: any) => {
                        return service.findModelDbXrefByModelId(inputArtifact.object.modelId)
                            .pipe(concatMap((modelDbXref: ModelDbXrefEntity) => {
                                // load metadata of model
                                return this.service('ModelService')
                                    .pipe(concatMap((modelService: any) => {
                                        return modelService.buildTableMetadata(modelDbXref.model, 'mongo')
                                            .pipe(concatMap((metadata: Metadata) => {
                                                return connect$(modelDbXref.dbId)
                                                    .pipe(map(() => {
                                                        const client: any = DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId));
                                                        client._modelsMap.get(inputArtifact.object.modelId).metadata = metadata;
                                                        return client;
                                                    }));
                                            }));
                                    }));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                const model = client._modelsMap.get(inputArtifact.object.modelId);
                const metadata: Metadata = model.metadata;
                const _client: Db = client;
                return from(_client.listCollections({name: model.tableName}).toArray())
                    .pipe(concatMap((collections: any[]) => {
                        if (collections.length)
                            return of(null);

                        return from(_client.createCollection(model.tableName));
                    }))
                    .pipe(concatMap(() => {
                        // check indexes
                        const indexColumns = new Map(Array.from(Object.keys(metadata)).filter((key: string) => metadata[key].options?.index).map((key: string) => [key, metadata[key]]));

                        const _collection: Collection = _client.collection(model.tableName);


                        return from(_collection.listIndexes().toArray())
                            .pipe(concatMap((indexes: any) => {
                                const keys = new Map(indexes.map((idx: any) => [Object.keys(idx.key)[0], idx]));
                                return from(Array.from(indexColumns.keys()))
                                    .pipe(filter((column: string) => !keys.has(column)))
                                    .pipe(concatMap((column: string) => {
                                        const _col = indexColumns.get(column);
                                        return from(_collection.createIndex({[column]: 1}, _col.options.index));
                                    }), toArray());
                            }));
                    }));

            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result;
                return outputArtifact;
            }));
    }

    @Hook('85aa4347-d54c-491e-9a1c-5c0d2abddd95')
    public findOne(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.modelId)
                    throw new Error('Invalid modelId');

                if (DatabaseMongoHook.connectionByModel.has(inputArtifact.object.modelId))
                    return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));


                return this.service('ModelDbXrefService')
                    .pipe(concatMap((service: any) => {
                        return service.findModelDbXrefByModelId(inputArtifact.object.modelId)
                            .pipe(concatMap((modelDbXref: ModelDbXrefEntity) => {
                                const connectArtifact = new NickProcessArtfact();
                                connectArtifact.kind = ArtfactKind.ANY;
                                connectArtifact.object = {dbId: modelDbXref.dbId};

                                return this.authenticate(connectArtifact);
                            }))
                            .pipe(concatMap(() => {
                                return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                let model = client._modelsMap.get(inputArtifact.object.modelId);
                const _collection: Collection = client.collection(model.tableName);
                return from(_collection.findOne(inputArtifact.object.filter));
                //     .pipe(map(() => {
                //
                //     }))
                // return new Observable((observer: Observer<any>) => {
                //     _collection.findOne(inputArtifact.object.filter, (err: any, result: any) => {
                //         if (err)
                //             observer.error(err);
                //         else if (!result)
                //             observer.next(null);
                //         else
                //             observer.next(result);
                //
                //         observer.complete();
                //     });
                // });
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result;
                return outputArtifact;
            }));
    }

    @Hook('d2c6246a-bc3d-4971-b515-5441e8a0a6c3')
    public find(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.modelId)
                    throw new Error('Invalid modelId');

                if (DatabaseMongoHook.connectionByModel.has(inputArtifact.object.modelId))
                    return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));


                return this.service('ModelDbXrefService')
                    .pipe(concatMap((service: any) => {
                        return service.findModelDbXrefByModelId(inputArtifact.object.modelId)
                            .pipe(concatMap((modelDbXref: ModelDbXrefEntity) => {
                                const connectArtifact = new NickProcessArtfact();
                                connectArtifact.kind = ArtfactKind.ANY;
                                connectArtifact.object = {dbId: modelDbXref.dbId};

                                return this.authenticate(connectArtifact);
                            }))
                            .pipe(concatMap(() => {
                                return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                let model = client._modelsMap.get(inputArtifact.object.modelId);
                const _collection: Collection = client.collection(model.tableName);
                return from(_collection.find(inputArtifact.object.filter).toArray());
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result;
                return outputArtifact;
            }));
    }

    @Hook('858ae5f98-cb8a-44ca-90ee-cea7e4604691')
    public findOneAndUpdateOLD(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.modelId)
                    throw new Error('Invalid modelId');

                if (DatabaseMongoHook.connectionByModel.has(inputArtifact.object.modelId))
                    return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));


                return this.service('ModelDbXrefService')
                    .pipe(concatMap((service: any) => {
                        return service.findModelDbXrefByModelId(inputArtifact.object.modelId)
                            .pipe(concatMap((modelDbXref: ModelDbXrefEntity) => {
                                const connectArtifact = new NickProcessArtfact();
                                connectArtifact.kind = ArtfactKind.ANY;
                                connectArtifact.object = {dbId: modelDbXref.dbId};

                                return this.authenticate(connectArtifact);
                            }))
                            .pipe(concatMap(() => {
                                return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                let model = client._modelsMap.get(inputArtifact.object.modelId);
                const _collection: Collection = client.collection(model.tableName);
                return from(_collection.findOneAndUpdate(inputArtifact.object.filter, {$set: inputArtifact.object.data}, inputArtifact.object.options));
                // return new Observable((observer: Observer<any>) => {
                //     _collection.findOneAndUpdate(inputArtifact.object.filter, {$set: inputArtifact.object.data}, inputArtifact.object.options, (err: any, result: any) => {
                //         if (err)
                //             observer.error(err);
                //         else
                //             observer.next(result);
                //         observer.complete();
                //     });
                // });
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result.value;
                return outputArtifact;
            }));
    }

    @Hook('c5f32174-63b3-4329-922d-bd86672aea9e')
    public findOneAndUpdate(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.modelId)
                    throw new Error('Invalid modelId');

                if (DatabaseMongoHook.connectionByModel.has(inputArtifact.object.modelId))
                    return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));


                return this.service('ModelDbXrefService')
                    .pipe(concatMap((service: any) => {
                        return service.findModelDbXrefByModelId(inputArtifact.object.modelId)
                            .pipe(concatMap((modelDbXref: ModelDbXrefEntity) => {
                                const connectArtifact = new NickProcessArtfact();
                                connectArtifact.kind = ArtfactKind.ANY;
                                connectArtifact.object = {dbId: modelDbXref.dbId};

                                return this.authenticate(connectArtifact);
                            }))
                            .pipe(concatMap(() => {
                                return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                let model = client._modelsMap.get(inputArtifact.object.modelId);
                const _collection: Collection = client.collection(model.tableName);
                return from(_collection.findOneAndUpdate(inputArtifact.object.filter, {$set: inputArtifact.object.data}, inputArtifact.object.options));

                // return new Observable((observer: Observer<any>) => {
                //     _collection.findOneAndUpdate(inputArtifact.object.filter, {$set: inputArtifact.object.data}, inputArtifact.object.options, (err: any, result: any) => {
                //         if (err)
                //             observer.error(err);
                //         else
                //             observer.next(result);
                //         observer.complete();
                //     });
                // });
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result.value;
                return outputArtifact;
            }));
    }

    @Hook('038927da-72d4-4b1c-a98d-1f03710d0847')
    public insertMany(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.modelId)
                    throw new Error('Invalid modelId');

                if (DatabaseMongoHook.connectionByModel.has(inputArtifact.object.modelId))
                    return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));


                return this.service('ModelDbXrefService')
                    .pipe(concatMap((service: any) => {
                        return service.findModelDbXrefByModelId(inputArtifact.object.modelId)
                            .pipe(concatMap((modelDbXref: ModelDbXrefEntity) => {
                                const connectArtifact = new NickProcessArtfact();
                                connectArtifact.kind = ArtfactKind.ANY;
                                connectArtifact.object = {dbId: modelDbXref.dbId};

                                return this.authenticate(connectArtifact);
                            }))
                            .pipe(concatMap(() => {
                                return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                let model = client._modelsMap.get(inputArtifact.object.modelId);
                const _collection: Collection = client.collection(model.tableName);
                return from(_collection.insertMany(inputArtifact.object.data, inputArtifact.object.options));

                // return new Observable((observer: Observer<any>) => {
                //     _collection.insertMany(inputArtifact.object.data, inputArtifact.object.options, (err: any, result: any) => {
                //         if (err)
                //             observer.error(err);
                //         else
                //             observer.next(result);
                //         observer.complete();
                //     });
                // });
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result.value;
                return outputArtifact;
            }));
    }

    @Hook('83d3dad4-c1a5-4c0b-b363-078c72e09336')
    public findAndModify(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.modelId)
                    throw new Error('Invalid modelId');

                if (DatabaseMongoHook.connectionByModel.has(inputArtifact.object.modelId))
                    return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));


                return this.service('ModelDbXrefService')
                    .pipe(concatMap((service: any) => {
                        return service.findModelDbXrefByModelId(inputArtifact.object.modelId)
                            .pipe(concatMap((modelDbXref: ModelDbXrefEntity) => {
                                const connectArtifact = new NickProcessArtfact();
                                connectArtifact.kind = ArtfactKind.ANY;
                                connectArtifact.object = {dbId: modelDbXref.dbId};

                                return this.authenticate(connectArtifact);
                            }))
                            .pipe(concatMap(() => {
                                return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                let model = client._modelsMap.get(inputArtifact.object.modelId);
                const _collection: Collection = client.collection(model.tableName);
                return from(_collection.findOneAndUpdate(inputArtifact.object.filter, inputArtifact.object.update, inputArtifact.object.options));
                // return new Observable((observer: Observer<any>) => {
                //     _collection.findAndModify(inputArtifact.object.data, inputArtifact.object.options, (err: any, result: any) => {
                //         if (err)
                //             observer.error(err);
                //         else
                //             observer.next(result);
                //         observer.complete();
                //     });
                // });
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result.value;
                return outputArtifact;
            }));
    }

    @Hook('c65317fc-f888-46c1-809a-aa921a53fb2c')
    public deleteOne(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.modelId)
                    throw new Error('Invalid modelId');

                if (DatabaseMongoHook.connectionByModel.has(inputArtifact.object.modelId))
                    return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));


                return this.service('ModelDbXrefService')
                    .pipe(concatMap((service: any) => {
                        return service.findModelDbXrefByModelId(inputArtifact.object.modelId)
                            .pipe(concatMap((modelDbXref: ModelDbXrefEntity) => {
                                const connectArtifact = new NickProcessArtfact();
                                connectArtifact.kind = ArtfactKind.ANY;
                                connectArtifact.object = {dbId: modelDbXref.dbId};

                                return this.authenticate(connectArtifact);
                            }))
                            .pipe(concatMap(() => {
                                return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                let model = client._modelsMap.get(inputArtifact.object.modelId);
                const _collection: Collection = client.collection(model.tableName);
                return from(_collection.deleteOne(inputArtifact.object.data, inputArtifact.object.options));

                // return new Observable((observer: Observer<any>) => {
                //     _collection.deleteOne(inputArtifact.object.data, inputArtifact.object.options, (err: any, result: any) => {
                //         if (err)
                //             observer.error(err);
                //         else
                //             observer.next(result);
                //         observer.complete();
                //     });
                // });
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result.value;
                return outputArtifact;
            }));
    }

    @Hook('ce933db6-154c-4b90-bef2-e27d52a50fa5')
    public deleteMany(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.modelId)
                    throw new Error('Invalid modelId');

                if (DatabaseMongoHook.connectionByModel.has(inputArtifact.object.modelId))
                    return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));


                return this.service('ModelDbXrefService')
                    .pipe(concatMap((service: any) => {
                        return service.findModelDbXrefByModelId(inputArtifact.object.modelId)
                            .pipe(concatMap((modelDbXref: ModelDbXrefEntity) => {
                                const connectArtifact = new NickProcessArtfact();
                                connectArtifact.kind = ArtfactKind.ANY;
                                connectArtifact.object = {dbId: modelDbXref.dbId};

                                return this.authenticate(connectArtifact);
                            }))
                            .pipe(concatMap(() => {
                                return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                let model = client._modelsMap.get(inputArtifact.object.modelId);
                const _collection: Collection = client.collection(model.tableName);
                return from(_collection.deleteMany(inputArtifact.object.filter, inputArtifact.object.options));

                // return new Observable((observer: Observer<any>) => {
                //     _collection.deleteOne(inputArtifact.object.data, inputArtifact.object.options, (err: any, result: any) => {
                //         if (err)
                //             observer.error(err);
                //         else
                //             observer.next(result);
                //         observer.complete();
                //     });
                // });
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result.value;
                return outputArtifact;
            }));
    }

    @Hook('80370238-9df4-4482-a073-0827811e3d58')
    public findOneAndDelete(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.modelId)
                    throw new Error('Invalid modelId');

                if (DatabaseMongoHook.connectionByModel.has(inputArtifact.object.modelId))
                    return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));


                return this.service('ModelDbXrefService')
                    .pipe(concatMap((service: any) => {
                        return service.findModelDbXrefByModelId(inputArtifact.object.modelId)
                            .pipe(concatMap((modelDbXref: ModelDbXrefEntity) => {
                                const connectArtifact = new NickProcessArtfact();
                                connectArtifact.kind = ArtfactKind.ANY;
                                connectArtifact.object = {dbId: modelDbXref.dbId};

                                return this.authenticate(connectArtifact);
                            }))
                            .pipe(concatMap(() => {
                                return of(DatabaseMongoHook.connections.get(DatabaseMongoHook.connectionByModel.get(inputArtifact.object.modelId)));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                let model = client._modelsMap.get(inputArtifact.object.modelId);
                const _collection: Collection = client.collection(model.tableName);
                return from(_collection.findOneAndDelete(inputArtifact.object.data, inputArtifact.object.options));

                // return new Observable((observer: Observer<any>) => {
                //     _collection.findOneAndDelete(inputArtifact.object.data, inputArtifact.object.options, (err: any, result: any) => {
                //         if (err)
                //             observer.error(err);
                //         else
                //             observer.next(result);
                //         observer.complete();
                //     });
                // });
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result.value;
                return outputArtifact;
            }));
    }
}
